/**
 * Usage tracking (test phase): small events queued in memory and posted in batches every 20 s and on
 * page hide. One "active" event per minute in which the person typed, clicked or scrolled.
 * The server drops everything when the workspace switch is off; the page also stops sending.
 */
type Ev = { kind: string; boardId?: string; detail?: string };
const queue: Ev[] = [];
let enabled = true;
let currentBoard: string | undefined;
let lastInput = 0;
let lastMinute = -1;
let started = false;

export function setUsageEnabled(on: boolean) {
  enabled = on;
}
export function setUsageBoard(boardId: string | undefined) {
  currentBoard = boardId;
}

export function trackUsage(kind: string, detail?: string, boardId?: string) {
  if (!enabled) return;
  queue.push({ kind, boardId: boardId ?? currentBoard, detail });
  if (queue.length >= 50) flush();
}

function flush() {
  if (queue.length === 0) return;
  const body = JSON.stringify({ events: queue.splice(0, 100) });
  try {
    if (navigator.sendBeacon?.("/api/usage", new Blob([body], { type: "application/json" }))) return;
  } catch {}
  void fetch("/api/usage", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    credentials: "include",
    keepalive: true,
  }).catch(() => {});
}

export function installUsageTracking() {
  if (started) return;
  started = true;
  const onInput = () => {
    lastInput = Date.now();
    const minute = Math.floor(lastInput / 60_000);
    if (minute !== lastMinute && document.visibilityState === "visible") {
      lastMinute = minute;
      trackUsage("active");
    }
  };
  for (const ev of ["pointerdown", "keydown", "wheel", "touchstart"] as const)
    window.addEventListener(ev, onInput, { passive: true, capture: true });
  setInterval(flush, 20_000);
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush());
}
